/**
 * SAM-1031: the privacy boundary for SvelteKit's error fallbacks.
 *
 * Both `handleError` hooks previously forwarded the raw `Error` to PostHog.
 * `captureException` serialises `message` and `stack`, and a SvelteKit error
 * message routinely quotes the failing payload — a journal note, an email, or
 * an Authorization header echoed back by a failed fetch.
 *
 * The contract mirrors `apps/backend/core/telemetry_privacy.py`: allowlist
 * first, fail closed. A value is emitted only when it is provably a
 * compile-time constant of this application (a route id, an error constructor
 * name); anything else collapses to a fixed constant.
 */

/** Emitted whenever a value cannot be proven safe. */
export const UNKNOWN = 'unknown';
/** Emitted when no route matched, or the route id fails the charset check. */
export const UNMATCHED_ROUTE = 'unmatched';

export const SERVER_ERROR_EVENT = 'server_error';
export const CLIENT_ERROR_EVENT = 'client_error';

/** Error constructor names are ASCII identifiers; anything else is suspect. */
const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]{0,63}$/;
/** A SvelteKit route id: literal segments plus `[param]` / `(group)` markers. */
const ROUTE_ID = /^\/[A-Za-z0-9/_.[\]()-]{0,199}$/;

export interface ErrorEnvelopeInput {
	status: number;
	routeId: string | null;
	error: unknown;
}

export interface ErrorEnvelope {
	[key: string]: string | number;
	error_class: string;
	route: string;
	status: number;
}

/** Return the stable constructor name — never the message or the stack. */
export function errorClass(error: unknown): string {
	if (!(error instanceof Error)) return UNKNOWN;
	const name = error.constructor?.name;
	return typeof name === 'string' && IDENTIFIER.test(name) ? name : UNKNOWN;
}

/**
 * Return the SvelteKit route id, which is a route *template*
 * (`/app/trackers/[id]/edit`) and never a resolved path — so no dynamic
 * parameter, and therefore no identifier, can travel in it.
 */
export function routeTemplate(routeId: string | null): string {
	if (typeof routeId !== 'string' || !ROUTE_ID.test(routeId)) return UNMATCHED_ROUTE;
	return routeId;
}

/** Build the allowlisted, content-free, identity-free error envelope. */
export function errorEnvelope(input: ErrorEnvelopeInput): ErrorEnvelope {
	return {
		error_class: errorClass(input.error),
		route: routeTemplate(input.routeId),
		status: input.status
	};
}

export interface ClientErrorSink {
	has_opted_in_capturing(): boolean;
	capture(event: string, properties: Record<string, string | number>): void;
}

export type ClientErrorOutcome = 'skipped-404' | 'no-consent' | 'captured';

/**
 * Report a client-side error as an allowlisted envelope, gated on the same
 * explicit analytics opt-in as every other client event (SAM-194). PostHog's
 * own `opt_out_capturing_by_default` would drop the event anyway; checking here
 * keeps the guarantee visible and testable rather than implicit in SDK config.
 */
export function captureClientError(
	sink: ClientErrorSink | null,
	input: ErrorEnvelopeInput
): ClientErrorOutcome {
	if (input.status === 404) return 'skipped-404';
	if (!sink?.has_opted_in_capturing()) return 'no-consent';

	sink.capture(CLIENT_ERROR_EVENT, errorEnvelope(input));
	return 'captured';
}
